import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { X, UserPlus } from "lucide-react";

// Searchable client picker for the Matter form. Selecting a client stores a
// stable client_id; the free-text name is preserved for display/back-compat.
export function ClientAutocomplete({ value, clientId, onChange, required, testid }) {
  const { t } = useT();
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [linkedClient, setLinkedClient] = useState(null);

  useEffect(() => {
    if (clientId && !linkedClient) {
      api.get(`/clients/${clientId}`).then((r) => setLinkedClient(r.data.client)).catch(() => {});
    }
    if (!clientId) setLinkedClient(null);
  }, [clientId]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", registration_number: "", vat_number: "", country: "LT" });
  const [warning, setWarning] = useState("");
  const [saving, setSaving] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const close = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", {
          params: { q: query, status: includeInactive ? "" : "active" },
        });
        setResults(data.slice(0, 20));
      } catch (e) {
        setResults([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [query, open, includeInactive]);

  const select = (c) => {
    setLinkedClient(c);
    setQuery(c.name);
    setOpen(false);
    onChange({ client: c.name, client_id: c.id });
  };

  const onType = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    if (clientId && v !== (linkedClient?.name ?? value)) onChange({ client: v, client_id: "" });
    else if (!clientId) onChange({ client: v, client_id: "" });
  };

  const clear = () => {
    setQuery("");
    setLinkedClient(null);
    onChange({ client: "", client_id: "" });
  };

  const createClient = async (allowDuplicate) => {
    setSaving(true);
    setWarning("");
    try {
      const { data } = await api.post("/clients", { ...newClient, allow_duplicate: allowDuplicate });
      select(data);
      setCreateOpen(false);
      setNewClient({ name: "", registration_number: "", vat_number: "", country: "LT" });
    } catch (e) {
      if (e?.response?.status === 409) {
        setWarning(e.response.data?.detail || "Duplicate");
      } else {
        setWarning(e?.response?.data?.detail || "Error");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-1.5 relative" ref={boxRef}>
      <div className="flex gap-2">
        <Input
          id="mf-client"
          data-testid={testid}
          required={required}
          value={query}
          onChange={onType}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        {clientId && (
          <button type="button" onClick={clear} data-testid="client-clear"
            className="shrink-0 p-2 text-slate-400 hover:text-slate-700" title={t("common.cancel")}>
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {clientId && (
        <div className="text-[11px] text-green-700 mt-1" data-testid="client-linked-badge">
          ✓ {[linkedClient?.name || value, linkedClient?.registration_number, linkedClient?.vat_number].filter(Boolean).join(" · ")}
        </div>
      )}
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="client-search-results">
          <label className="flex items-center gap-2 px-3 py-2 text-[11px] text-slate-500 border-b border-slate-100">
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)}
              data-testid="client-include-inactive" className="rounded border-slate-300" />
            {t("form.client_include_inactive")}
          </label>
          {results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400" data-testid="client-no-results">{t("form.client_no_results")}</div>
          ) : (
            results.map((c) => (
              <button key={c.id} type="button" onClick={() => select(c)} data-testid={`client-option-${c.id}`}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50">
                <div className="text-sm font-medium text-slate-800">
                  {c.name}
                  {c.status === "Inactive" && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border bg-slate-100 text-slate-500 border-slate-200">{t("val.Inactive")}</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500">
                  {[c.registration_number, c.vat_number].filter(Boolean).join(" · ") || "—"}
                </div>
              </button>
            ))
          )}
          <button type="button" onClick={() => { setCreateOpen(true); setOpen(false); setNewClient((n) => ({ ...n, name: query })); }}
            data-testid="client-create-open"
            className="w-full text-left px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50 flex items-center gap-1.5">
            <UserPlus className="h-3.5 w-3.5" /> {t("form.client_create_new")}
          </button>
        </div>
      )}
      {!open && !createOpen && (
        <button type="button" onClick={() => setCreateOpen(true)} data-testid="client-create-open-inline"
          className="mt-1.5 text-[11px] font-semibold text-amber-700 hover:underline flex items-center gap-1">
          <UserPlus className="h-3 w-3" /> {t("form.client_create_new")}
        </button>
      )}
      {createOpen && (
        <div className="mt-2 border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50" data-testid="client-create-form">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("form.client_create_title")}</div>
          <Input data-testid="client-create-name" placeholder={t("import.col_name")} required
            value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} className="h-8 text-sm bg-white" />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="client-create-reg" placeholder={t("import.col_reg")}
              value={newClient.registration_number} onChange={(e) => setNewClient({ ...newClient, registration_number: e.target.value })} className="h-8 text-sm bg-white" />
            <Input data-testid="client-create-vat" placeholder={t("import.col_vat")}
              value={newClient.vat_number} onChange={(e) => setNewClient({ ...newClient, vat_number: e.target.value })} className="h-8 text-sm bg-white" />
          </div>
          {warning && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="client-create-warning">
              {warning}
              <button type="button" onClick={() => createClient(true)} data-testid="client-create-anyway"
                className="ml-2 font-semibold underline">
                {t("form.client_create_anyway")}
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <Button type="button" size="sm" disabled={saving || !newClient.name.trim()} onClick={() => createClient(false)}
              data-testid="client-create-save" className="bg-slate-900 hover:bg-slate-800 h-8">
              {t("common.save")}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => { setCreateOpen(false); setWarning(""); }}
              data-testid="client-create-cancel" className="h-8">
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
